import { all, put, call, takeLatest, select, take } from 'redux-saga/effects';
import interactionChannels from './channels/interaction';
import segmentationSelectors from '../selectors/segmentation';
import actions from '../actions';

const {
  getBoundingRect,
  getOffsetBetweenBoundingRectAndPoint,
} = Hover.Machete.PolygonUtils;

const raycaster = new THREE.Raycaster();

/**
 *  Normalize the pointer coordinates for the client
 * @param {number} clientX - X-Coordinates
 * @param {number} clientY - Y-coordinates
 * @param {object} boundingClientRect - react client boundaries
 */
function normalizedPointerCoordinates(
  { clientX, clientY },
  boundingClientRect,
) {
  const { width, height, right, bottom } = boundingClientRect;

  // since isn't bounding X & Y in MS Edge we need to calculate
  // the normalized X & Y coordinates using the right, width, bottom, and height
  return new THREE.Vector2(
    ((clientX - (right - width)) / width) * 2 - 1,
    -(((clientY - (bottom - height)) / height) * 2) + 1,
  );
}

/**
 *  This is the pointers first hit
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 * @param {PointerEvent} pointerEvent - state of a DOM event produced by a pointer such as the geometry of the contact point
 * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent
 * @param {Object3D} objects - base class for most objects in three.js and provides a set of properties and methods for manipulating objects in 3D space.
 * @see https://threejs.org/docs/#api/en/core/Object3D
 */
function firstHit(controller, pointerEvent, objects) {
  const { viewport, camera } = controller.viewer;

  const pointerVector = normalizedPointerCoordinates(
    pointerEvent,
    viewport.container.getBoundingClientRect(),
  );

  raycaster.setFromCamera(pointerVector, camera);

  const hits = raycaster.intersectObjects(objects);

  if (hits.length > 0) {
    const {
      point,
      face: { normal },
      object: {
        element: { outerLoop },
        nodeUUID,
      },
    } = hits[0];

    const facadeBounds = getBoundingRect(outerLoop, normal);
    const facadeOffset = getOffsetBetweenBoundingRectAndPoint(
      facadeBounds,
      point,
    );

    // TODO: follow naming convention for 3D world coords and 2D facade coords
    return {
      coords: point,
      facadeOffset,
      uuid: nodeUUID,
    };
  }
  return null;
}

/**
 * Retrieve the poitier's first hit
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 * @param {PointerEvent} pointerEvent - state of a DOM event produced by a pointer such as the geometry of the contact point
 * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent
 */
function* getFirstHit(controller, pointerEvent) {
  const objects = yield select(segmentationSelectors.getObjectsForRaycaster);
  return firstHit(controller, pointerEvent, objects);
}

/**
 * Event while pointer is moving
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 * @param {PointerEvent} pointerEvent - state of a DOM event produced by a pointer such as the geometry of the contact point
 * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent
 */
function* pointerMove(controller, pointerEvent) {
  const { config, hit: oldHit } = yield select(
    segmentationSelectors.getPointer,
  );
  const newHit = yield call(getFirstHit, controller, pointerEvent);
  if (!oldHit && !newHit) return;
  if (config.uuidOnly && oldHit && newHit && newHit.uuid === oldHit.uuid)
    return;
  yield put(actions.segmentation.setPointerHit(newHit));
  yield put(actions.segmentation.pointerMove(pointerEvent, newHit));
}

/**
 * Event while pointer is down
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 * @param {PointerEvent} pointerEvent - state of a DOM event produced by a pointer such as the geometry of the contact point
 * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent
 */
function* pointerDown(controller, pointerEvent) {
  const hit = yield call(getFirstHit, controller, pointerEvent);
  yield put(actions.segmentation.pointerDown(pointerEvent, hit));
}

/**
 * Event while pointer is up
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 * @param {PointerEvent} pointerEvent - state of a DOM event produced by a pointer such as the geometry of the contact point
 * @see https://developer.mozilla.org/en-US/docs/Web/API/PointerEvent
 */
function* pointerUp(controller, pointerEvent) {
  const hit = yield call(getFirstHit, controller, pointerEvent);
  yield put(actions.segmentation.pointerUp(pointerEvent, hit));
}

const interactionChannelSagaMap = {
  pointerDown,
  pointerUp,
  pointerMove,
};

/**
 * Handle the pointer events durning any given pointer action
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 */
function* handlePointer(controller) {
  while (true) {
    const {
      config: { enabledEvents },
    } = yield select(segmentationSelectors.getPointer);
    const forks = [];
    const channels = [];

    /* eslint-disable no-restricted-syntax */
    for (const [event, enabled] of Object.entries(enabledEvents)) {
      if (enabled) {
        const channel = yield call(interactionChannels[event], controller);
        const saga = takeLatest(
          channel,
          interactionChannelSagaMap[event],
          controller,
        );
        forks.push(saga);
        channels.push(channel);
      }
    }
    /* eslint-enable no-restricted-syntax */

    yield all(forks);
    yield take(actions.segmentation.disablePointer);

    channels.forEach((channel) => channel.close());
    // TODO: This doesn't do a thing, except for crash
    forks.forEach((f) => f.cancel());
  }
}

/**
 * Main saga for interaction
 * @param {*} controller - contains methods to manipulate the model, viewer and segmentation tree.
 */
export default function* interactionSagas(controller) {
  yield all([
    yield takeLatest(
      [
        actions.segmentation.enablePointer,
        actions.segmentation.updatePointerConfig,
      ],
      handlePointer,
      controller,
    ),
  ]);
}
